import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import './SlateSelect.css';

class SlateSelect extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isOpen: false,
      searchQuery: '',
      highlightedIndex: -1,
      selectedValue: props.value || props.defaultValue || null,
      dropdownPosition: { top: 0, left: 0, width: 0 },
    };
    this.containerRef = React.createRef();
    this.searchInputRef = React.createRef();
    this.optionsRef = React.createRef();
    this.dropdownRef = React.createRef();
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside);
    document.addEventListener('keydown', this.handleKeyDown);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
    document.removeEventListener('keydown', this.handleKeyDown);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.value !== this.props.value) {
      this.setState({ selectedValue: this.props.value });
    }
    if (prevProps.defaultValue !== this.props.defaultValue && !this.props.value) {
      this.setState({ selectedValue: this.props.defaultValue });
    }
  }

  handleClickOutside = (event) => {
    const container = this.containerRef.current;
    const dropdown = this.dropdownRef.current;
    const clickedInContainer = container && container.contains(event.target);
    const clickedInDropdown = dropdown && dropdown.contains(event.target);

    if (!clickedInContainer && !clickedInDropdown) {
      this.setState({ isOpen: false, searchQuery: '', highlightedIndex: -1 });
    }
  };

  updateDropdownPosition = () => {
    if (this.containerRef.current) {
      var rect = this.containerRef.current.getBoundingClientRect();
      var dropUp = this.props.dropUp;

      this.setState({
        dropdownPosition: {
          top: dropUp ? null : rect.bottom + window.scrollY + 4,
          bottom: dropUp ? (window.innerHeight - rect.top + 4) : null,
          left: rect.left + window.scrollX,
          width: rect.width,
        },
      });
    }
  };

  handleKeyDown = (event) => {
    const { isOpen, highlightedIndex } = this.state;
    const filteredOptions = this.getFilteredOptions();

    if (!isOpen) {
      if (event.key === 'Enter' || event.key === ' ') {
        const container = this.containerRef.current;
        if (container && document.activeElement === container.querySelector('.slate-select-trigger')) {
          event.preventDefault();
          this.toggleOpen();
        }
      }
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.setState({
          highlightedIndex: Math.min(highlightedIndex + 1, filteredOptions.length - 1),
        }, this.scrollToHighlighted);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.setState({
          highlightedIndex: Math.max(highlightedIndex - 1, 0),
        }, this.scrollToHighlighted);
        break;
      case 'Enter':
        event.preventDefault();
        if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          this.handleSelect(filteredOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        event.preventDefault();
        this.setState({ isOpen: false, searchQuery: '', highlightedIndex: -1 });
        break;
      default:
        break;
    }
  };

  scrollToHighlighted = () => {
    const { highlightedIndex } = this.state;
    if (this.optionsRef.current && highlightedIndex >= 0) {
      const option = this.optionsRef.current.children[highlightedIndex];
      if (option) {
        option.scrollIntoView({ block: 'nearest' });
      }
    }
  };

  toggleOpen = () => {
    const { isOpen } = this.state;
    const { disabled } = this.props;

    if (disabled) return;

    if (!isOpen) {
      this.updateDropdownPosition();
    }

    this.setState(
      { isOpen: !isOpen, searchQuery: '', highlightedIndex: -1 },
      () => {
        if (this.state.isOpen && this.searchInputRef.current) {
          this.searchInputRef.current.focus();
        }
      }
    );
  };

  handleSearch = (event) => {
    this.setState({ searchQuery: event.target.value, highlightedIndex: 0 });
  };

  handleSelect = (option) => {
    const { onChange } = this.props;
    this.setState({
      selectedValue: option.value,
      isOpen: false,
      searchQuery: '',
      highlightedIndex: -1,
    });
    if (onChange) {
      onChange(option.value);
    }
  };

  getFilteredOptions = () => {
    var options = this.props.options;
    var searchQuery = this.state.searchQuery;

    if (!options) return [];
    if (!searchQuery) return options;

    var query = searchQuery.toLowerCase();
    return options.filter(
      function(option) {
        return option.text.toLowerCase().includes(query) ||
          option.value.toLowerCase().includes(query);
      }
    );
  };

  getSelectedOption = () => {
    var options = this.props.options;
    var selectedValue = this.state.selectedValue;
    if (!options) return null;
    return options.find(function(option) { return option.value === selectedValue; });
  };

  renderDropdown() {
    const { searchable, minDropdownWidth } = this.props;
    const { searchQuery, highlightedIndex, dropdownPosition } = this.state;
    const filteredOptions = this.getFilteredOptions();

    var dropdownWidth = dropdownPosition.width;
    if (minDropdownWidth && dropdownWidth < minDropdownWidth) {
      dropdownWidth = minDropdownWidth;
    }

    var dropdownStyle = {
      position: 'absolute',
      left: dropdownPosition.left,
      minWidth: dropdownWidth,
      width: 'auto',
      zIndex: 9999,
    };
    if (dropdownPosition.bottom !== null && dropdownPosition.bottom !== undefined) {
      dropdownStyle.bottom = dropdownPosition.bottom;
    } else {
      dropdownStyle.top = dropdownPosition.top;
    }

    return ReactDOM.createPortal(
      <div
        ref={this.dropdownRef}
        className="slate-select-dropdown"
        style={dropdownStyle}
      >
        {searchable !== false && (
          <div className="slate-select-search">
            <span className="material-symbols-outlined slate-select-search-icon">search</span>
            <input
              ref={this.searchInputRef}
              type="text"
              className="slate-select-search-input"
              placeholder="Search..."
              value={searchQuery}
              onChange={this.handleSearch}
              autoComplete="off"
            />
          </div>
        )}
        <div className="slate-select-options" ref={this.optionsRef} role="listbox">
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <button
                key={option.value}
                type="button"
                className={`slate-select-option ${
                  option.value === this.state.selectedValue ? 'slate-select-option--selected' : ''
                } ${index === highlightedIndex ? 'slate-select-option--highlighted' : ''}`}
                onClick={() => this.handleSelect(option)}
                onMouseEnter={() => this.setState({ highlightedIndex: index })}
                role="option"
                aria-selected={option.value === this.state.selectedValue}
              >
                <span className="slate-select-option-text">{option.text}</span>
                {option.value === this.state.selectedValue && (
                  <span className="material-symbols-outlined slate-select-option-check">check</span>
                )}
              </button>
            ))
          ) : (
            <div className="slate-select-empty">No results found</div>
          )}
        </div>
      </div>,
      document.body
    );
  }

  render() {
    const { placeholder, disabled, className } = this.props;
    const { isOpen } = this.state;
    const selectedOption = this.getSelectedOption();

    var classNames = ['slate-select'];
    if (isOpen) classNames.push('slate-select--open');
    if (disabled) classNames.push('slate-select--disabled');
    if (className) classNames.push(className);

    return (
      <div
        ref={this.containerRef}
        className={classNames.join(' ')}
      >
        <button
          type="button"
          className="slate-select-trigger"
          onClick={this.toggleOpen}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          <span className={`slate-select-value ${!selectedOption ? 'slate-select-value--placeholder' : ''}`}>
            {selectedOption ? selectedOption.text : placeholder}
          </span>
          <span className={`slate-select-chevron ${isOpen ? 'slate-select-chevron--open' : ''}`}>
            <span className="material-symbols-outlined">expand_more</span>
          </span>
        </button>

        {isOpen && this.renderDropdown()}
      </div>
    );
  }
}

SlateSelect.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
    })
  ).isRequired,
  value: PropTypes.string,
  defaultValue: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  searchable: PropTypes.bool,
  className: PropTypes.string,
  minDropdownWidth: PropTypes.number,
  dropUp: PropTypes.bool,
};

SlateSelect.defaultProps = {
  placeholder: 'Select an option',
  disabled: false,
  searchable: true,
};

export default SlateSelect;
